import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import styles from './LiveCommunication.module.css';

const LiveCommunication = ({ onBack }) => {
  const { t } = useTranslation('liveCommunication');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [moderators, setModerators] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [autoScroll, setAutoScroll] = useState(true);

  const tabs = [
    { id: 'dashboard', label: t('tabs.dashboard'), icon: 'dashboard' },
    { id: 'rooms', label: t('tabs.rooms'), icon: 'meeting_room' },
    { id: 'users', label: t('tabs.users'), icon: 'people' },
    { id: 'reports', label: t('tabs.reports'), icon: 'report_problem' },
    { id: 'moderation', label: t('tabs.moderation'), icon: 'security' },
    { id: 'analytics', label: t('tabs.analytics'), icon: 'analytics' },
    { id: 'categories', label: t('tabs.categories'), icon: 'category' },
    { id: 'moderators', label: t('tabs.moderators'), icon: 'admin_panel_settings' },
    { id: 'announcements', label: t('tabs.announcements'), icon: 'campaign' },
    { id: 'settings', label: t('tabs.settings'), icon: 'settings' },
    { id: 'logs', label: t('tabs.logs'), icon: 'history' }
  ];

  useEffect(() => {
    loadSampleData();
  }, []);

  const loadSampleData = () => {
    // Sample data for rooms
    setRooms([
      {
        id: 1,
        title: 'English Conversation',
        creator: 'Sarah Johnson',
        type: 'voice',
        category: 'english',
        status: 'normal',
        users: 12,
        messages: 156,
        created: '2024-01-15T10:00:00'
      },
      {
        id: 2,
        title: 'IELTS Practice',
        creator: 'Mike Chen',
        type: 'mixed',
        category: 'ielts',
        status: 'flagged',
        users: 8,
        messages: 89,
        created: '2024-01-15T11:30:00'
      },
      {
        id: 3,
        title: 'French Beginners',
        creator: 'Maria Garcia',
        type: 'text',
        category: 'french',
        status: 'monitored',
        users: 15,
        messages: 234,
        created: '2024-01-15T09:15:00'
      }
    ]);

    // Sample data for users
    setUsers([
      {
        id: 1,
        name: 'John Smith',
        email: 'john.smith@example.com',
        status: 'online',
        lastActivity: '2024-01-15T14:30:00',
        roomsJoined: 5,
        messagesSent: 45
      },
      {
        id: 2,
        name: 'Emma Wilson',
        email: 'emma.wilson@example.com',
        status: 'offline',
        lastActivity: '2024-01-15T13:45:00',
        roomsJoined: 3,
        messagesSent: 23
      },
      {
        id: 3,
        name: 'David Brown',
        email: 'david.brown@example.com',
        status: 'online',
        lastActivity: '2024-01-15T14:25:00',
        roomsJoined: 7,
        messagesSent: 67
      }
    ]);

    // Sample data for reports
    setReports([
      {
        id: 1,
        title: 'Inappropriate language in English room',
        reporter: 'Emma Wilson',
        reportedUser: 'John Smith',
        type: 'inappropriate',
        priority: 'high',
        status: 'pending',
        date: '2024-01-15T14:20:00',
        description: 'User was using offensive language in the English conversation room.'
      },
      {
        id: 2,
        title: 'Spam messages in IELTS room',
        reporter: 'Mike Chen',
        reportedUser: 'David Brown',
        type: 'spam',
        priority: 'medium',
        status: 'investigating',
        date: '2024-01-15T13:45:00',
        description: 'User was sending repeated promotional messages.'
      },
      {
        id: 3,
        title: 'Harassment in French room',
        reporter: 'Maria Garcia',
        reportedUser: 'Sarah Johnson',
        type: 'harassment',
        priority: 'high',
        status: 'resolved',
        date: '2024-01-15T12:30:00',
        description: 'User was harassing other participants in the French room.'
      }
    ]);

    // Sample data for moderators
    setModerators([
      {
        id: 1,
        name: 'Admin User',
        role: 'Senior Moderator',
        actionsTaken: 156,
        reportsResolved: 89,
        status: 'active'
      },
      {
        id: 2,
        name: 'Moderator 1',
        role: 'Moderator',
        actionsTaken: 78,
        reportsResolved: 45,
        status: 'active'
      },
      {
        id: 3,
        name: 'Moderator 2',
        role: 'Junior Moderator',
        actionsTaken: 34,
        reportsResolved: 23,
        status: 'inactive'
      }
    ]);

    // Sample data for announcements
    setAnnouncements([
      {
        id: 1,
        title: 'System Maintenance',
        message: 'Scheduled maintenance on January 20th from 2-4 AM UTC.',
        type: 'site-wide',
        date: '2024-01-15T10:00:00',
        active: true
      },
      {
        id: 2,
        title: 'New Features Available',
        message: 'Voice rooms are now available for all users!',
        type: 'site-wide',
        date: '2024-01-14T15:30:00',
        active: true
      },
      {
        id: 3,
        title: 'IELTS Room Guidelines',
        message: 'Please follow the IELTS practice guidelines in IELTS rooms.',
        type: 'category-specific',
        date: '2024-01-13T09:15:00',
        active: false
      }
    ]);

    // Sample analytics data
    setAnalytics({
      activeRooms: 24,
      onlineUsers: 1247,
      pendingReports: 7,
      messagesToday: 45678
    });
  };

  const renderDashboard = () => (
    <div>
      {/* Live Stats */}
      <div className={styles.liveStatsGrid}>
        <div className={`${styles.statCard} ${styles.urgent}`}>
          <div className={styles.statIcon}>
            <span className="material-icons">meeting_room</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.activeRooms')}</h3>
            <p className={styles.statNumber}>{analytics.activeRooms}</p>
            <span className={`${styles.statChange} ${styles.positive}`}>+3 {t('stats.inLast5Min')}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <span className="material-icons">people</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.onlineUsers')}</h3>
            <p className={styles.statNumber}>{analytics.onlineUsers}</p>
            <span className={`${styles.statChange} ${styles.positive}`}>+89 {t('stats.inLast5Min')}</span>
          </div>
        </div>
        <div className={`${styles.statCard} ${styles.warning}`}>
          <div className={styles.statIcon}>
            <span className="material-icons">report_problem</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.pendingReports')}</h3>
            <p className={styles.statNumber}>{analytics.pendingReports}</p>
            <span className={`${styles.statChange} ${styles.negative}`}>+2 {t('stats.inLast5Min')}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <span className="material-icons">chat</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.messagesToday')}</h3>
            <p className={styles.statNumber}>{analytics.messagesToday}</p>
            <span className={`${styles.statChange} ${styles.positive}`}>+12% {t('stats.vsYesterday')}</span>
          </div>
        </div>
      </div>

      {/* Live Activity Feed */}
      <div className={styles.liveActivitySection}>
        <div className={styles.sectionSubheader}>
          <h2>{t('liveActivityFeed.title')}</h2>
          <div className={styles.feedControls}>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`} onClick={() => setAutoScroll(!autoScroll)}>
              <span className="material-icons">auto_fix_high</span>
              {t('liveActivityFeed.autoScroll')}
            </button>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>
              <span className="material-icons">clear_all</span>
              {t('liveActivityFeed.clear')}
            </button>
          </div>
        </div>
        <div className={styles.activityFeed}>
          <div className={styles.activityItem}>
            <div className={styles.activityIcon}>
              <span className="material-icons">event</span>
            </div>
            <div className={styles.activityContent}>
              <p><strong>{t('liveActivityFeed.newRoomCreated', { title: 'English Conversation', creator: 'Sarah Johnson' })}</strong></p>
              <span className={styles.activityTime}>{t('liveActivityFeed.minutesAgo', { count: 2 })}</span>
            </div>
          </div>
          <div className={`${styles.activityItem} ${styles.urgent}`}>
            <div className={styles.activityIcon}>
              <span className="material-icons">report_problem</span>
            </div>
            <div className={styles.activityContent}>
              <p><strong>{t('liveActivityFeed.reportSubmitted', { description: 'Inappropriate language in IELTS room' })}</strong></p>
              <span className={styles.activityTime}>{t('liveActivityFeed.minutesAgo', { count: 5 })}</span>
            </div>
          </div>
          <div className={styles.activityItem}>
            <div className={styles.activityIcon}>
              <span className="material-icons">person_add</span>
            </div>
            <div className={styles.activityContent}>
              <p><strong>{t('liveActivityFeed.userJoined', { user: 'Emma Wilson', room: 'French Beginners' })}</strong></p>
              <span className={styles.activityTime}>{t('liveActivityFeed.minutesAgo', { count: 8 })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={styles.quickActions}>
        <h2>{t('quickActions.title')}</h2>
        <div className={styles.actionButtons}>
          <button className={styles.actionBtn} onClick={() => setActiveTab('rooms')}>
            <span className="material-icons">visibility</span>
            <span>{t('quickActions.viewAllRooms')}</span>
          </button>
          <button className={styles.actionBtn} onClick={() => setActiveTab('reports')}>
            <span className="material-icons">report_problem</span>
            <span>{t('quickActions.reviewReports')}</span>
          </button>
          <button className={styles.actionBtn} onClick={() => setActiveTab('users')}>
            <span className="material-icons">person_search</span>
            <span>{t('quickActions.searchUsers')}</span>
          </button>
          <button className={styles.actionBtn}>
            <span className="material-icons">campaign</span>
            <span>{t('quickActions.sendAnnouncement')}</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderRooms = () => (
    <div>
      <div className={styles.filtersBar}>
        <div className={styles.searchBox}>
          <span className="material-icons">search</span>
          <input type="text" placeholder={t('filters.searchRooms')} />
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">{t('filters.allTypes')}</option>
            <option value="voice">{t('filters.voice')}</option>
            <option value="text">{t('filters.text')}</option>
            <option value="mixed">{t('filters.mixed')}</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">{t('filters.allCategories')}</option>
            <option value="english">English</option>
            <option value="ielts">IELTS</option>
            <option value="french">French</option>
            <option value="coding">Coding</option>
            <option value="general">General</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">{t('filters.allStatus')}</option>
            <option value="normal">{t('filters.normal')}</option>
            <option value="flagged">{t('filters.flagged')}</option>
            <option value="monitored">{t('filters.monitored')}</option>
          </select>
        </div>
      </div>

      <div className={styles.roomsGrid}>
        {rooms.map(room => (
          <div key={room.id} className={`${styles.roomCard} ${styles[room.status]}`}>
            <div className={styles.roomHeader}>
              <div>
                <div className={styles.roomTitle}>{room.title}</div>
                <div className={styles.roomCreator}>by {room.creator}</div>
              </div>
              <span className={styles.roomStatus}>{room.status}</span>
            </div>
            <div className={styles.roomContent}>
              <div className={styles.roomMeta}>
                <div className={styles.roomMetaItem}>
                  <div className={styles.roomMetaLabel}>Type</div>
                  <div className={styles.roomMetaValue}>{room.type}</div>
                </div>
                <div className={styles.roomMetaItem}>
                  <div className={styles.roomMetaLabel}>Category</div>
                  <div className={styles.roomMetaValue}>{room.category}</div>
                </div>
                <div className={styles.roomMetaItem}>
                  <div className={styles.roomMetaLabel}>Users</div>
                  <div className={styles.roomMetaValue}>{room.users}</div>
                </div>
                <div className={styles.roomMetaItem}>
                  <div className={styles.roomMetaLabel}>Messages</div>
                  <div className={styles.roomMetaValue}>{room.messages}</div>
                </div>
              </div>
              <div className={styles.roomActions}>
                <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                  <span className="material-icons">visibility</span>
                  View
                </button>
                <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>
                  <span className="material-icons">flag</span>
                  Flag
                </button>
                <button className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`}>
                  <span className="material-icons">close</span>
                  Close
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderUsers = () => (
    <div>
      <div className={styles.userSearchSection}>
        <div className={`${styles.searchBox} ${styles.large}`}>
          <span className="material-icons">search</span>
          <input type="text" placeholder="Search by username, email, or IP..." />
          <button className={`${styles.btn} ${styles.btnPrimary}`}>Search</button>
        </div>
      </div>

      <div className={styles.userResults}>
        {users.map(user => (
          <div key={user.id} className={styles.userCard}>
            <div className={styles.userAvatar}>
              {user.name.charAt(0)}
            </div>
            <div className={styles.userInfo}>
              <div className={styles.userName}>{user.name}</div>
              <div className={styles.userDetails}>{user.email}</div>
            </div>
            <span className={`${styles.userStatus} ${styles[user.status]}`}>
              {user.status}
            </span>
            <div className={styles.userActions}>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                <span className="material-icons">visibility</span>
              </button>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>
                <span className="material-icons">edit</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.bannedUsersSection}>
        <h2>Banned Users</h2>
        <div className={styles.bannedUsersTableContainer}>
          <table className={styles.bannedUsersTable}>
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>IP Address</th>
                <th>Ban Reason</th>
                <th>Banned By</th>
                <th>Ban Date</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>spam_user</td>
                <td>spam@example.com</td>
                <td>192.168.1.100</td>
                <td>Spam messages</td>
                <td>Admin</td>
                <td>2024-01-10</td>
                <td>2024-02-10</td>
                <td>
                  <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                    <span className="material-icons">visibility</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderReports = () => (
    <div>
      <div className={styles.filtersBar}>
        <div className={styles.searchBox}>
          <span className="material-icons">search</span>
          <input type="text" placeholder="Search reports..." />
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Types</option>
            <option value="spam">Spam</option>
            <option value="hate">Hate Speech</option>
            <option value="inappropriate">Inappropriate Content</option>
            <option value="harassment">Harassment</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="investigating">Investigating</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      <div className={styles.reportsList}>
        {reports.map(report => (
          <div key={report.id} className={`${styles.reportCard} ${report.priority === 'high' ? styles.highPriority : ''}`}>
            <div className={styles.reportHeader}>
              <div className={styles.reportInfo}>
                <div className={styles.reportTitle}>{report.title}</div>
                <div className={styles.reportMeta}>
                  <span>Reporter: {report.reporter}</span>
                  <span>Reported: {report.reportedUser}</span>
                  <span>{new Date(report.date).toLocaleDateString()}</span>
                </div>
              </div>
              <span className={`${styles.reportPriority} ${styles[report.priority]}`}>
                {report.priority}
              </span>
            </div>
            <div className={styles.reportContent}>
              <div className={styles.reportDescription}>{report.description}</div>
            </div>
            <div className={styles.reportActions}>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                <span className="material-icons">visibility</span> Review
              </button>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnWarning}`}>
                <span className="material-icons">warning</span> Warn
              </button>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`}>
                <span className="material-icons">block</span> Ban
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderModeration = () => (
    <div>
      <div className={styles.moderationLogContainer}>
        <table className={styles.moderationLogTable}>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Moderator</th>
              <th>Action</th>
              <th>Target User</th>
              <th>Room</th>
              <th>Reason</th>
              <th>Duration</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>2024-01-15 14:30</td>
              <td>Admin User</td>
              <td>Ban User</td>
              <td>spam_user</td>
              <td>English Conversation</td>
              <td>Spam messages</td>
              <td>30 days</td>
              <td><span className={`${styles.statusBadge} ${styles.statusActive}`}>Active</span></td>
            </tr>
            <tr>
              <td>2024-01-15 13:45</td>
              <td>Moderator 1</td>
              <td>Mute User</td>
              <td>john_doe</td>
              <td>IELTS Practice</td>
              <td>Inappropriate language</td>
              <td>1 hour</td>
              <td><span className={`${styles.statusBadge} ${styles.statusActive}`}>Active</span></td>
            </tr>
            <tr>
              <td>2024-01-15 12:20</td>
              <td>Admin User</td>
              <td>Close Room</td>
              <td>-</td>
              <td>Spam Room</td>
              <td>Multiple violations</td>
              <td>Permanent</td>
              <td><span className={`${styles.statusBadge} ${styles.statusCompleted}`}>Completed</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div>
      <div className={styles.analyticsOverview}>
        <div className={styles.analyticsCard}>
          <h3>Peak Hours</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">schedule</span>
            <p>Peak hours chart will be displayed here</p>
          </div>
        </div>
        <div className={styles.analyticsCard}>
          <h3>Most Active Rooms</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">trending_up</span>
            <p>Active rooms chart will be displayed here</p>
          </div>
        </div>
        <div className={styles.analyticsCard}>
          <h3>User Engagement</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">people</span>
            <p>Engagement chart will be displayed here</p>
          </div>
        </div>
        <div className={styles.analyticsCard}>
          <h3>Moderation Activity</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">security</span>
            <p>Moderation activity chart will be displayed here</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCategories = () => (
    <div>
      <div className={styles.categoriesGrid}>
        <div className={styles.categoryCard}>
          <div className={styles.categoryHeader}>
            <div className={styles.categoryName}>English</div>
            <span className={styles.roomStatus}>Active</span>
          </div>
          <div className={styles.categoryStats}>
            <div className={styles.categoryStat}>
              <div className={styles.categoryStatNumber}>12</div>
              <div className={styles.categoryStatLabel}>Active Rooms</div>
            </div>
            <div className={styles.categoryStat}>
              <div className={styles.categoryStatNumber}>156</div>
              <div className={styles.categoryStatLabel}>Users</div>
            </div>
          </div>
          <div className={styles.categoryActions}>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>View</button>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>Edit</button>
          </div>
        </div>
        <div className={styles.categoryCard}>
          <div className={styles.categoryHeader}>
            <div className={styles.categoryName}>IELTS</div>
            <span className={styles.roomStatus}>Active</span>
          </div>
          <div className={styles.categoryStats}>
            <div className={styles.categoryStat}>
              <div className={styles.categoryStatNumber}>8</div>
              <div className={styles.categoryStatLabel}>Active Rooms</div>
            </div>
            <div className={styles.categoryStat}>
              <div className={styles.categoryStatNumber}>89</div>
              <div className={styles.categoryStatLabel}>Users</div>
            </div>
          </div>
          <div className={styles.categoryActions}>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>View</button>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>Edit</button>
          </div>
        </div>
        <div className={styles.categoryCard}>
          <div className={styles.categoryHeader}>
            <div className={styles.categoryName}>French</div>
            <span className={styles.roomStatus}>Active</span>
          </div>
          <div className={styles.categoryStats}>
            <div className={styles.categoryStat}>
              <div className={styles.categoryStatNumber}>5</div>
              <div className={styles.categoryStatLabel}>Active Rooms</div>
            </div>
            <div className={styles.categoryStat}>
              <div className={styles.categoryStatNumber}>67</div>
              <div className={styles.categoryStatLabel}>Users</div>
            </div>
          </div>
          <div className={styles.categoryActions}>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>View</button>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>Edit</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderModerators = () => (
    <div>
      <div className={styles.moderatorsList}>
        {moderators.map(moderator => (
          <div key={moderator.id} className={styles.moderatorCard}>
            <div className={styles.moderatorHeader}>
              <div className={styles.moderatorAvatar}>
                {moderator.name.charAt(0)}
              </div>
              <div className={styles.moderatorInfo}>
                <h3>{moderator.name}</h3>
                <div className={styles.moderatorRole}>{moderator.role}</div>
              </div>
            </div>
            <div className={styles.moderatorStats}>
              <div className={styles.moderatorStat}>
                <div className={styles.moderatorStatNumber}>{moderator.actionsTaken}</div>
                <div className={styles.moderatorStatLabel}>Actions</div>
              </div>
              <div className={styles.moderatorStat}>
                <div className={styles.moderatorStatNumber}>{moderator.reportsResolved}</div>
                <div className={styles.moderatorStatLabel}>Reports</div>
              </div>
            </div>
            <div className={styles.moderatorActions}>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                <span className="material-icons">visibility</span> View
              </button>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>
                <span className="material-icons">edit</span> Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnnouncements = () => (
    <div>
      <div className={styles.announcementsList}>
        {announcements.map(announcement => (
          <div key={announcement.id} className={styles.announcementCard}>
            <div className={styles.announcementHeader}>
              <div className={styles.announcementTitle}>{announcement.title}</div>
              <div className={styles.announcementMeta}>
                <span>{announcement.type}</span>
                <span>{new Date(announcement.date).toLocaleDateString()}</span>
              </div>
            </div>
            <div className={styles.announcementContent}>
              <div className={styles.announcementMessage}>{announcement.message}</div>
            </div>
            <div className={styles.announcementActions}>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                <span className="material-icons">edit</span> Edit
              </button>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>
                <span className="material-icons">delete</span> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSettings = () => (
    <div>
      <div className={styles.settingsGrid}>
        <div className={styles.settingsCard}>
          <h3>Room Limits</h3>
          <div className={styles.settingItem}>
            <label htmlFor="maxRooms">Maximum Active Rooms</label>
            <input type="number" id="maxRooms" defaultValue="100" min="1" max="1000" />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="maxUsersPerRoom">Maximum Users per Room</label>
            <input type="number" id="maxUsersPerRoom" defaultValue="50" min="1" max="200" />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="autoCloseMinutes">Auto-close Inactive Rooms (minutes)</label>
            <input type="number" id="autoCloseMinutes" defaultValue="30" min="5" max="1440" />
          </div>
        </div>

        <div className={styles.settingsCard}>
          <h3>Access Control</h3>
          <div className={styles.settingItem}>
            <label htmlFor="allowGuestAccess">Allow Guest Access</label>
            <input type="checkbox" id="allowGuestAccess" defaultChecked />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="requireEmail">Require Email Verification</label>
            <input type="checkbox" id="requireEmail" />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="defaultLanguage">Default Language Filter</label>
            <select id="defaultLanguage">
              <option value="all">All Languages</option>
              <option value="english">English Only</option>
              <option value="french">French Only</option>
              <option value="spanish">Spanish Only</option>
            </select>
          </div>
        </div>

        <div className={styles.settingsCard}>
          <h3>Moderation Settings</h3>
          <div className={styles.settingItem}>
            <label htmlFor="autoModeration">Enable Auto-moderation</label>
            <input type="checkbox" id="autoModeration" />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="spamThreshold">Spam Detection Threshold</label>
            <input type="number" id="spamThreshold" defaultValue="5" min="1" max="20" />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="reportThreshold">Auto-flag Threshold (reports)</label>
            <input type="number" id="reportThreshold" defaultValue="3" min="1" max="10" />
          </div>
        </div>

        <div className={styles.settingsCard}>
          <h3>Monitoring</h3>
          <div className={styles.settingItem}>
            <label htmlFor="enableVoiceMonitoring">Enable Voice Activity Monitoring</label>
            <input type="checkbox" id="enableVoiceMonitoring" />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="enableIPTracking">Enable IP and Country Tracking</label>
            <input type="checkbox" id="enableIPTracking" defaultChecked />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="logRetentionDays">Log Retention (days)</label>
            <input type="number" id="logRetentionDays" defaultValue="30" min="1" max="365" />
          </div>
        </div>
      </div>

      <div className={styles.settingsActions}>
        <button className={`${styles.btn} ${styles.btnPrimary}`}>Save Settings</button>
        <button className={`${styles.btn} ${styles.btnSecondary}`}>Reset to Default</button>
      </div>
    </div>
  );

  const renderLogs = () => (
    <div>
      <div className={styles.filtersBar}>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Logs</option>
            <option value="error">Errors</option>
            <option value="warning">Warnings</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <input type="date" />
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Modules</option>
            <option value="rooms">Rooms</option>
            <option value="users">Users</option>
            <option value="moderation">Moderation</option>
            <option value="system">System</option>
          </select>
        </div>
      </div>

      <div className={styles.logsContainer}>
        <table className={styles.logsTable}>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Level</th>
              <th>Module</th>
              <th>Message</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>2024-01-15 14:30:25</td>
              <td><span className={`${styles.statusBadge} ${styles.statusActive}`}>INFO</span></td>
              <td>Rooms</td>
              <td>New room created: English Conversation</td>
              <td>Room ID: 123, Creator: Sarah Johnson</td>
            </tr>
            <tr>
              <td>2024-01-15 14:28:12</td>
              <td><span className={`${styles.statusBadge} ${styles.statusWarning}`}>WARN</span></td>
              <td>Users</td>
              <td>User reported for inappropriate language</td>
              <td>User: john_doe, Room: IELTS Practice</td>
            </tr>
            <tr>
              <td>2024-01-15 14:25:45</td>
              <td><span className={`${styles.statusBadge} ${styles.statusActive}`}>INFO</span></td>
              <td>System</td>
              <td>Backup completed successfully</td>
              <td>Size: 2.3GB, Duration: 15 minutes</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'rooms': return renderRooms();
      case 'users': return renderUsers();
      case 'reports': return renderReports();
      case 'moderation': return renderModeration();
      case 'analytics': return renderAnalytics();
      case 'categories': return renderCategories();
      case 'moderators': return renderModerators();
      case 'announcements': return renderAnnouncements();
      case 'settings': return renderSettings();
      case 'logs': return renderLogs();
      default: return renderDashboard();
    }
  };

  return (
    <div className={styles.liveCommunicationWrapper}>
      <div className={styles.liveCommunicationContent}>
        {onBack && (
          <button className={styles.backToCourseManagement} onClick={onBack}>
            <span className="material-icons">arrow_back</span>
            {t('backToCourseManagement')}
          </button>
        )}

        <div className={styles.tabsContainer}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="material-icons">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className={styles.contentArea}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default LiveCommunication; 